// Typed activation helpers for a customer's config.ts.
//
// WHY these exist: `RaventicSdkConfig`/`CustomerConfig` types every widget slot with
// the options params set to `any` (see the note on RaventicSdkConfig in
// ./config.ts). A single config type can't carry a DIFFERENT options type per
// widget / per carousel, so the slots accept any `card`/`cardOptions` (and dropdown
// `contentItem`/`contentBlock`, modal/results `promoContentItem`/`promoContentBlock`
// + options) combination without checking it. These
// identity helpers restore the checks at the call site.
//
//   search: { dropdown: defineDropdown({ activateOn, config: { card: MyCard, cardOptions } }) },
//   rce: { carousels: [defineCarousel({ type: "product", …, widgetConfig: { card, cardOptions } })] },
//
// Rules, applied independently to EACH component/options pair — card/cardOptions
// (all four helpers, every carousel type), contentItem/contentItemOptions and
// contentBlock/contentBlockOptions (dropdown), promoContentItem/promoContentItemOptions
// and promoContentBlock/promoContentBlockOptions (modal + results):
//   - neither set → OK (the SDK default renders);
//   - component whose options type has ≥1 REQUIRED key → `*Options` REQUIRED;
//   - component whose options type is all-optional, `unknown`/`any`, or accepts
//     `undefined`/`void` (a bare `CardComponent` infers `void`) → `*Options` optional;
//   - `*Options` WITHOUT a component → error (the SDK defaults ignore them, so it
//     would be dead config);
//   - options not matching the component → error.
// Plus one cross-pair rule per item/block duo: `contentBlock` together with
// `contentItem` or `contentItemOptions` (dropdown), and `promoContentBlock` together
// with `promoContentItem` or `promoContentItemOptions` (modal + results) → error (a
// custom block replaces the whole group / promo box and search then ignores the item
// pair, so it would be dead config).
// Remaining limits: the rules apply only INSIDE a helper (an unwrapped widget in
// CustomerConfig is loose), and "required" is derived from the component's DECLARED
// options type (a component typed `CardComponent<{ x?: number }>` that still needs
// `x` at runtime isn't caught). A value whose type makes BOTH fields of a pair
// optional is rejected, because it could be either branch: a whole SDK-typed
// config (`SearchModalConfig<X>`, `Partial<…>`), a conditional spread
// `...(flag ? { card, cardOptions } : {})`, or a component typed `C | undefined`.
// Workarounds: build the whole activation conditionally
// (`flag ? defineModal({ …, card, cardOptions }) : defineModal({ … })`), write the
// config as a literal (`satisfies` if it lives in a variable), or leave that widget
// unwrapped. (Widening the pair to accept this would reopen the orphan gap.)
//
// Inference: from BOTH fields of a pair (no `NoInfer`), with `const` options params.
// WHY `const`: a component type (Preact `FunctionComponent<P>`: call signature +
// `defaultProps?: Partial<P>`) is INVARIANT in its options type, so it is not a
// contravariant inference site that could pin O; without `const` the options
// literal alone decided O and widened it (`{ buttonText: "default" }` →
// `{ buttonText: string }`), and a component typed with a literal union then failed
// to match — with the error landing on the component field. With `const` the
// component's declared type wins whenever the literal fits it, so a literal
// `cardOptions` needs no `as const` / `satisfies`, and a mismatch is an error AT the
// offending option value — TS2322 (e.g. `"promo"` not assignable to
// `"default" | "reco"`, `number` not assignable to `string`), or TS2353 for an
// excess key. (The same extra-key object passed as a variable is accepted: no
// excess-property check.) Keeping both fields as inference sources also lets an
// inline `card: (p) => …` get `p.options` contextually typed from `cardOptions` —
// as the `const`-inferred literal (`{ readonly buttonText: "default" }`), since
// nothing else constrains it. When neither is given the params fall to `unknown`
// (the default-card case). The RETURN types wrap the params in `NoInfer`: inside a
// `CustomerConfig` slot (typed `…Activation<any>`) TS otherwise also infers O from the
// contextual return type, and that candidate widened the literal again despite `const`.
// Type-level tests (incl. the in-slot case): test/define.typecheck.ts.
//
// Error messages: a mismatch points at the offending option value. A missing or
// orphaned field gives a long TS2322 whose last line typically reads
// "Property 'cardOptions' is missing" / "Property 'card' is missing". With several
// pairs (every search helper: dropdown card + content, modal/results card + promo
// content) TS may elaborate against ANOTHER pair's branch, so that last
// line can name the wrong field (e.g. "Property 'card' is missing" for a missing
// `contentItemOptions`). The block+item rules (contentBlock + contentItem,
// promoContentBlock + promoContentItem) can surface as an unrelated-looking
// mismatch on an item field (seen: `Type 'string' is not assignable to type
// '"default" | "reco"'` on `contentItemOptions`, where the item pair alone is valid) or
// as "… is not assignable to type 'undefined'". The error
// LOCATION (the `config` literal, or a field inside it) is reliable; check each pair
// against the rules above.
//
// Names mirror the config keys (`search.dropdown` → defineDropdown, …) in the
// familiar `defineX` helper style. Each returns its argument typed as the plain
// activation type with the inferred params, which is assignable to the
// `any`-parameterised slot in CustomerConfig. defineCarousel returns it unchanged
// and is optional (an unwrapped carousel compiles, it just isn't checked). The three
// SEARCH helpers are required: they also add the activation's `widget` (the search
// widget class it draws with), which the slot requires. That is what bundles a
// widget's code only when the customer's config defines it — products/search.ts
// never imports the classes itself (see the note on DropdownActivation in
// ./config.ts). The assistant's helper, defineAssistantModal, lives in ./assistant.ts
// (the `@raventic/sdk/assistant` entry) and reuses CardPair below.
//
// Dropdown chat: `chatbot` with `enabled: true` requires `chatbotUi` (the assistant's
// `dropdownChat`, imported from "@raventic/sdk/assistant"), and `chatbotUi` without an
// enabled `chatbot` is an error (dead config). Search renders no chat without the
// injected UI, so a forgotten import would otherwise only show as a console.error.

import type { CardComponent } from "@raventic/sdk-common";
import { DropdownWidget, ModalWidget, ResultsWidget } from "@raventic/sdk-search-v1";
import type {
  ChatbotConfig,
  DropdownChatUi,
  ContentItemComponent,
  ContentBlockComponent,
  PromoContentItemComponent,
  PromoContentBlockComponent,
} from "@raventic/sdk-search-v1";
import type {
  DropdownActivation,
  ModalActivation,
  ResultsActivation,
  CarouselActivation,
  ExperimentConfig,
  ArmConfig,
  StagedConfigs,
} from "./config";
import { stagedRuntime } from "./staged-runtime";
// Per-language default texts (iteration 26), one module per widget: each is imported only by
// that widget's helper below, so a widget's packs ship only with that widget.
import { withDropdownTexts } from "./texts/dropdown";
import type { DropdownTextKey } from "./texts/dropdown";
import { withModalTexts } from "./texts/modal";
import type { ModalTextKey } from "./texts/modal";
import { withResultsTexts } from "./texts/results";
import type { ResultsTextKey } from "./texts/results";

// ── Pair rules ───────────────────────────────────────────────────────────────
// `true` when an options type has at least one REQUIRED key. POLICY: options a
// component DECLARES as required must be supplied. (A card really receives
// `cardOptions` as-is; content components' `options?: O` is optional in the SDK
// props, so for them this is a declared-intent rule, not a render requirement.)
// `false` for `unknown`/`any` (no declared options), anything accepting `undefined`
// — incl. `void`, which a bare `CardComponent` (no type arg) infers as — and
// all-optional objects (`{} extends O`).
type OptionsRequired<O> = unknown extends O
  ? false
  : undefined extends O
    ? false
    : {} extends O
      ? false
      : true;

// One component/options pair, keyed by its two field names, as a two-way union:
//   - NEITHER field set (the SDK default renders), or
//   - the component set, with its options REQUIRED if OptionsRequired<O>, else optional.
// Options without a component match neither branch → error: the SDK defaults ignore
// them (`card ? cardOptions : buildDefaultCardOptions(…)`; the content defaults don't
// read *Options), so they would be dead config. O is still inferred from BOTH fields.
type PairUnset<CK extends string, OK extends string> = { [K in CK]?: undefined } & {
  [K in OK]?: undefined;
};
type PairSet<CK extends string, OK extends string, C, O> = { [K in CK]: C } & (OptionsRequired<O> extends true
  ? { [K in OK]: O }
  : { [K in OK]?: O });
type ComponentPair<CK extends string, OK extends string, C, O> = PairUnset<CK, OK> | PairSet<CK, OK, C, O>;

// Exported (types only) for defineAssistantModal in ./assistant.ts, whose modal config
// carries the same card pair.
export type CardPair<O> = ComponentPair<"card", "cardOptions", CardComponent<O>, O>;
type ContentItemPair<O> = ComponentPair<"contentItem", "contentItemOptions", ContentItemComponent<O>, O>;
// Cross-pair rule (dropdown): a custom `contentBlock` takes over the whole group and
// search then IGNORES contentItem/contentItemOptions (the block renders with only
// contentBlockOptions) — dead config, like an orphaned `*Options`. So either the
// item pair (block unset) or a set block with the item pair unset.
type ContentPairs<CO, BO> =
  | (ContentItemPair<CO> & PairUnset<"contentBlock", "contentBlockOptions">)
  | (PairUnset<"contentItem", "contentItemOptions"> &
      PairSet<"contentBlock", "contentBlockOptions", ContentBlockComponent<BO>, BO>);

// Same cross-pair rule for the results/modal promo content box (search 5.2.0): a custom
// `promoContentBlock` takes over the whole box and search ignores the item pair.
type PromoContentItemPair<O> = ComponentPair<
  "promoContentItem",
  "promoContentItemOptions",
  PromoContentItemComponent<O>,
  O
>;
type PromoContentPairs<PO, PBO> =
  | (PromoContentItemPair<PO> & PairUnset<"promoContentBlock", "promoContentBlockOptions">)
  | (PairUnset<"promoContentItem", "promoContentItemOptions"> &
      PairSet<"promoContentBlock", "promoContentBlockOptions", PromoContentBlockComponent<PBO>, PBO>);

// The dropdown chat pair: an ENABLED `chatbot` needs the injected `chatbotUi`;
// `chatbotUi` alone is dead config. A disabled `chatbot` (enabled: false) needs nothing.
type ChatPair =
  | { chatbot?: (ChatbotConfig & { enabled: false }) | undefined; chatbotUi?: undefined }
  | { chatbot: ChatbotConfig & { enabled: true }; chatbotUi: DropdownChatUi };

export type CardKeys = "card" | "cardOptions";
type ChatKeys = "chatbot" | "chatbotUi";
type ContentKeys = "contentItem" | "contentItemOptions" | "contentBlock" | "contentBlockOptions";
type PromoContentKeys =
  | "promoContentItem"
  | "promoContentItemOptions"
  | "promoContentBlock"
  | "promoContentBlockOptions";

// The widget's UI texts that have a per-language default (./texts/*): OPTIONAL in what the
// customer writes (same types otherwise, so a wrong-typed text is still an error); the helper
// fills the ones left `undefined`, so the activation it returns (and the SDK) gets them all.
type OptionalTexts<C, K extends keyof C> = Omit<C, K> & Partial<Pick<C, K>>;

// Helper INPUT types: the activation with its config's pair fields swapped for the
// pair rules above, and its default-backed texts optional (every other field unchanged).
// The card pair is independent of the content pairs, so the dropdown intersects the two
// (and modal/results intersect the card pair with the promo-content pairs).
type Dropdown = DropdownActivation;
// `widget` is left out of every search input: the helper adds it.
type DropdownInput<O, CO, BO> = Omit<Dropdown, "config" | "widget"> & {
  config: OptionalTexts<Omit<Dropdown["config"], CardKeys | ContentKeys | ChatKeys>, DropdownTextKey> &
    CardPair<O> &
    ContentPairs<CO, BO> &
    ChatPair;
};
type WithResultsPairs<
  A extends { config: object },
  O,
  PO,
  PBO,
  TK extends keyof Omit<A["config"], CardKeys | PromoContentKeys>,
> = Omit<A, "config" | "widget"> & {
  config: OptionalTexts<Omit<A["config"], CardKeys | PromoContentKeys>, TK> &
    CardPair<O> &
    PromoContentPairs<PO, PBO>;
};
// Each carousel variant rebuilt with its own `widgetConfig` (its own Service), so
// the `type` discriminant still narrows. DISTRIBUTIVE over T: for a non-literal
// `type` (T a union) it yields a union of whole variants. A plain `Omit` over the
// union would keep only the COMMON keys and silently drop productId/categoryId.
type CarouselVariant<T extends CarouselActivation["type"], O> = T extends unknown
  ? Omit<Extract<CarouselActivation, { type: T }>, "widgetConfig"> & {
      widgetConfig: Omit<Extract<CarouselActivation, { type: T }>["widgetConfig"], CardKeys> & CardPair<O>;
    }
  : never;

// DEFAULT TEXTS (iteration 26): each search helper also fills the UI texts its config leaves
// `undefined` from the built-in pack of the config's `locale` language (cs / sk / en / de, else
// en; ./texts/*), the dropdown's `chatbot.localization` per key too (with the chat-text merge
// its `chatbotUi` — the facade's `dropdownChat` — carries, so those ship only with the chat). A
// value the config sets wins, `""` included; a complete config object is kept as-is (never
// mutated, copied only when something is filled). Done HERE, once per activation, because
// every search widget passes through its helper (the slot requires the `widget` only a helper
// adds): a plain config, every arm of an experiment and the dev harness (which transforms the
// evaluated config) all get it.

// `search.dropdown`: checks card/cardOptions (O), contentItem/contentItemOptions
// (CO) and contentBlock/contentBlockOptions (BO), and rejects contentBlock combined
// with contentItem/contentItemOptions; checks the chat pair (ChatPair above).
export function defineDropdown<const O = unknown, const CO = unknown, const BO = unknown>(
  activation: DropdownInput<O, CO, BO>,
): DropdownActivation<NoInfer<O>, NoInfer<CO>, NoInfer<BO>> {
  return { ...activation, config: withDropdownTexts(activation.config), widget: DropdownWidget } as DropdownActivation<
    NoInfer<O>,
    NoInfer<CO>,
    NoInfer<BO>
  >;
}

// `search.modal`: checks card/cardOptions (O), promoContentItem/promoContentItemOptions
// (PO) and promoContentBlock/promoContentBlockOptions (PBO), and rejects
// promoContentBlock combined with promoContentItem/promoContentItemOptions.
export function defineModal<const O = unknown, const PO = unknown, const PBO = unknown>(
  activation: WithResultsPairs<ModalActivation, O, PO, PBO, ModalTextKey>,
): ModalActivation<NoInfer<O>, NoInfer<PO>, NoInfer<PBO>> {
  return { ...activation, config: withModalTexts(activation.config), widget: ModalWidget } as ModalActivation<
    NoInfer<O>,
    NoInfer<PO>,
    NoInfer<PBO>
  >;
}

// `search.results`: same pairs and rules as defineModal (the modal config extends the
// results config, so both carry the promo-content fields).
export function defineResults<const O = unknown, const PO = unknown, const PBO = unknown>(
  activation: WithResultsPairs<ResultsActivation, O, PO, PBO, ResultsTextKey>,
): ResultsActivation<NoInfer<O>, NoInfer<PO>, NoInfer<PBO>> {
  return { ...activation, config: withResultsTexts(activation.config), widget: ResultsWidget } as ResultsActivation<
    NoInfer<O>,
    NoInfer<PO>,
    NoInfer<PBO>
  >;
}

// One `rce.carousels` entry (any `type`): checks widgetConfig.card/cardOptions.
// Generic over the `type` T (a literal in the usual case, not the whole union) so
// the argument is checked against ONE variant: errors then name the real missing
// field instead of a mismatch against another variant's Service. A non-literal
// `type` makes T a union; CarouselVariant distributes, so that stays sound. O is inferred per carousel, so
// carousels with different card types can sit in the same array. The cast is
// type-only: TS can't relate the rebuilt variant to `Extract<…>` while T is generic.
export function defineCarousel<T extends CarouselActivation["type"], const O = unknown>(
  activation: CarouselVariant<T, O> & { type: T },
): Extract<CarouselActivation<NoInfer<O>>, { type: T }> {
  return activation as unknown as Extract<CarouselActivation<NoInfer<O>>, { type: T }>;
}

// A config experiment (iteration 18, ./ab.ts): `config` is N complete arms + one shared
// `analytics`. Identity at runtime (boot() recognises an experiment by its `arms` key);
// it exists for contextual typing — each arm is checked as an ArmConfig, so a stray
// `analytics` inside an arm is a type error (it belongs at the experiment level), and
// define* helpers used inside an arm are checked as usual. The id / weights rules are
// runtime-checked by boot() (a broken experiment runs arm 0, unrecorded).
//
//   export const config = defineExperiment({
//     id: "cards-2026-10",
//     analytics: { apiKey: "…" },
//     arms: [null, { search: { dropdown: defineDropdown({ … }) } }], // "0" = off, "1" = on
//   });
export function defineExperiment(experiment: ExperimentConfig): ExperimentConfig {
  return experiment;
}

// Staged configs (iteration 27, ./staged.ts): named complete configs next to the live one (a
// plain config or an experiment), shown only after `?rvn_staged=<name>` switches one on for the
// tab. Required for the `staged` key: it adds the staged runtime (./staged-runtime.ts: switching,
// <html> mark, badge) boot() calls, so that code ships only with staged configs. Each config is checked as
// an ArmConfig (no `analytics`: the top-level one is shared; `null` = no widgets). Names are
// runtime-checked by boot() (a broken staged block runs live).
//
//   export const config: CustomerConfig = {
//     analytics, search: { dropdown },
//     staged: defineStaged({ "new-cards": { search: { dropdown: newCardsDropdown } } }),
//   };
export function defineStaged(configs: Readonly<Record<string, ArmConfig | null>>): StagedConfigs {
  return { configs, runtime: stagedRuntime() };
}
